/* Campaign media and editor lifecycle. Commerce and Fancybox use existing theme paths. */
(() => {
  if (window.glpCampaign) return;
  const media = new Map();
  const docks = new Map();
  const scrollers = new Map();
  const tilts = new Map();
  const FINE = matchMedia("(hover: hover) and (pointer: fine)");
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  function initDock(rail) {
    if (docks.has(rail)) return;
    const dock = rail.querySelector('.glp-dock');
    const list = rail.querySelector('.glp-dock__list');
    if (!dock || !list) return;
    const controller = new AbortController();
    let queued = false;
    const update = () => {
      queued = false;
      const trigger = document.querySelector(rail.dataset.dockAfter);
      const visible = !trigger || trigger.getBoundingClientRect().bottom <= 48;
      const inverted = [...document.querySelectorAll('[data-dock-surface="dark"]')].some(el => {
        const r = el.getBoundingClientRect();
        return innerHeight - 24 >= r.top && innerHeight - 24 < r.bottom;
      });
      dock.classList.toggle('is-visible', visible);
      dock.classList.toggle('is-inverted', inverted);
      dock.setAttribute('aria-hidden', String(!visible));
      dock.inert = !visible;
    };
    const schedule = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
    window.addEventListener('scroll', schedule, { passive: true, signal: controller.signal });
    window.addEventListener('resize', schedule, { signal: controller.signal });
    const active = list.querySelector('[aria-current="page"]');
    if (active && list.scrollWidth > list.clientWidth) list.scrollLeft = Math.max(0, active.offsetLeft - (list.clientWidth - active.offsetWidth) / 2);
    docks.set(rail, () => controller.abort());
    update();
  }
  function initScroller(el) {
    if (scrollers.has(el)) return;
    const controller = new AbortController();
    const update = () => {
      el.classList.toggle('is-fade-start', el.scrollLeft > 4);
      el.classList.toggle('is-fade-end', el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
    };
    el.addEventListener('scroll', update, { passive: true, signal: controller.signal });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    scrollers.set(el, () => { controller.abort(); observer.disconnect(); });
    update();
  }
const clamp = (v) => Math.min(1, Math.max(-1, v));

function bindTilt(el) {
  const controller = new AbortController();
  const max = Number(el.dataset.tiltMax || 8);
  const area = (el.dataset.tiltArea && el.closest(el.dataset.tiltArea)) || el;
  let areaRect = null;
  let cardRect = null;
  let frame = 0;
  let next = null;

  const apply = () => {
    frame = 0;
    if (!next) return;
    const { x, y, gx, gy, over } = next;
    // the side under the cursor lifts toward the viewer (card pivoting on its axis)
    el.style.setProperty('--tilt-y', `${(-x * max).toFixed(2)}deg`);
    el.style.setProperty('--tilt-x', `${(y * max).toFixed(2)}deg`);
    el.style.setProperty('--glare-x', `${((gx + 1) * 50).toFixed(1)}%`);
    el.style.setProperty('--glare-y', `${((gy + 1) * 50).toFixed(1)}%`);
    el.classList.toggle('is-over', over);
    el.style.setProperty('--par-x', x.toFixed(3));
    el.style.setProperty('--par-y', y.toFixed(3));
  };

  const onMove = (e) => {
    if (!FINE.matches || reduce.matches) return;
    if (!areaRect) areaRect = area.getBoundingClientRect();
    if (!cardRect) cardRect = el.getBoundingClientRect();
    // tilt: normalised position across the whole hover area, -1 .. 1
    const x = clamp(((e.clientX - areaRect.left) / areaRect.width) * 2 - 1);
    const y = clamp(((e.clientY - areaRect.top) / areaRect.height) * 2 - 1);
    // light: position relative to the card itself, clamped to its edges
    const rgx = ((e.clientX - cardRect.left) / cardRect.width) * 2 - 1;
    const rgy = ((e.clientY - cardRect.top) / cardRect.height) * 2 - 1;
    const over = Math.abs(rgx) <= 1 && Math.abs(rgy) <= 1;
    next = { x, y, gx: clamp(rgx), gy: clamp(rgy), over };
    if (!frame) frame = requestAnimationFrame(apply);
  };

  const onEnter = (e) => {
    if (!FINE.matches || reduce.matches) return;
    areaRect = area.getBoundingClientRect();
    cardRect = el.getBoundingClientRect();
    el.classList.add('is-tilting');
    onMove(e);
  };

  const onLeave = () => {
    areaRect = null;
    cardRect = null;
    next = null;
    if (frame) { cancelAnimationFrame(frame); frame = 0; }
    el.classList.remove('is-tilting', 'is-over');
    ['--tilt-x', '--tilt-y', '--par-x', '--par-y'].forEach((p) => el.style.removeProperty(p));
    // leave the glare position where it was so it fades out in place
  };

  area.addEventListener('pointerenter', onEnter, { signal: controller.signal });
  area.addEventListener('pointermove', onMove, { signal: controller.signal });
  area.addEventListener('pointerleave', onLeave, { signal: controller.signal });
  area.addEventListener('pointercancel', onLeave, { signal: controller.signal });
  reduce.addEventListener('change', onLeave, { signal: controller.signal });
  FINE.addEventListener('change', onLeave, { signal: controller.signal });
  return () => { onLeave(); controller.abort(); };
}

  function init(root = document) {
    root.querySelectorAll(".rtnu-glp [data-tilt]").forEach(el => { if (!tilts.has(el)) tilts.set(el, bindTilt(el)); });
    root.querySelectorAll(".rtnu-glp [data-edge-fade]").forEach(initScroller);
    root.querySelectorAll(".rtnu-glp [data-guide-nav]").forEach(initDock);
    // Keep alternate-template preview navigation within this campaign during local QA.
    if (new URLSearchParams(location.search).get('view')?.startsWith('glp-')) {
      const previews = {
        'lp-glp-1-nutrition-guide': 'glp-guide', 'lp-glp-1-muscle-loss': 'glp-muscle',
        'lp-glp-1-digestion-support': 'glp-digestion', 'lp-glp-1-gut-barrier-support': 'glp-gut-barrier',
        'lp-glp-1-nutrient-gaps': 'glp-nutrient-gaps'
      };
      document.querySelectorAll('.rtnu-glp a[href]').forEach(a => {
        if (a.getAttribute('href').startsWith('#')) return;
        const url = new URL(a.href, location.href);
        const handle = url.pathname.split('/pages/')[1];
        if (url.origin === location.origin && previews[handle]) {
          url.searchParams.set('view', previews[handle]);
          a.href = url.href;
        }
      });
    }

    root.querySelectorAll('.rtnu-glp [data-glp-media], [data-glp-media]').forEach(wrapper => {
      if (media.has(wrapper)) return;
      const video = wrapper.querySelector('video');
      if (!video) return;
      const button = wrapper.querySelector('.glp-media__toggle');
      const controller = new AbortController();
      let visible = false, paused = false;
      const updateButton = () => {
        if (!button) return;
        const isPaused = video.paused || video.ended;
        button.classList.toggle('is-paused', isPaused);
        button.setAttribute('aria-label', isPaused ? 'Play animation' : 'Pause animation');
      };
      const sync = () => {
        const play = visible && !paused && !reduce.matches && !document.hidden;
        if (play) video.play().catch(() => video.classList.remove('is-playing')); else video.pause();
        if (reduce.matches) video.classList.remove('is-playing');
        if (button) {
          button.hidden = reduce.matches;
          updateButton();
        }
      };
      const observer = new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting;
        sync();
      }, { rootMargin: '100px' });
      observer.observe(wrapper);
      ['play', 'pause', 'ended', 'error'].forEach(event => video.addEventListener(event, updateButton, { signal: controller.signal }));
      video.addEventListener('playing', () => {
        if (!reduce.matches) video.classList.add('is-playing');
      }, { signal: controller.signal });
      video.addEventListener('error', () => video.classList.remove('is-playing'), { signal: controller.signal });
      button?.addEventListener('click', () => { paused = !video.paused; sync(); }, { signal: controller.signal });
      reduce.addEventListener('change', sync, { signal: controller.signal });
      document.addEventListener('visibilitychange', sync, { signal: controller.signal });
      media.set(wrapper, () => { video.pause(); observer.disconnect(); controller.abort(); });
      sync();
    });
    document.querySelectorAll('.rtnu-glp .glp-jump a[href^="#"]').forEach(link => {
      let id;
      try { id = decodeURIComponent(link.hash.slice(1)); } catch { id = ''; }
      const target = id && document.getElementById(id);
      if (link.parentElement) link.parentElement.hidden = !target;
    });

  }
  function dispose(root) {
    for (const [el, cleanup] of tilts) if (root.contains(el)) { cleanup(); tilts.delete(el); }
    for (const [el, cleanup] of scrollers) if (root.contains(el)) { cleanup(); scrollers.delete(el); }
    for (const [rail, cleanup] of docks) if (root.contains(rail)) { cleanup(); docks.delete(rail); }
    for (const [wrapper, cleanup] of media) if (root.contains(wrapper)) { cleanup(); media.delete(wrapper); }
  }
  window.glpCampaign = { init, dispose };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true }); else init();
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:section:unload', e => { dispose(e.target); requestAnimationFrame(() => init()); });
  document.addEventListener('shopify:section:reorder', () => init());
  document.addEventListener('shopify:block:select', e => {
    const details = e.target.closest('details');
    if (details) details.open = true;
  });
})();
